/**
 * ProfileFAQ — the "old structure" FAQ shown on every Profile page.
 * Three top-level tabs (Property Management · Residents · Commercial Business),
 * same content that ships on the marketing LandingPage. This lives on the
 * profile page so signed-in users can still reach every answer without
 * bouncing back out to the landing site.
 */
import { useEffect, useMemo, useState } from 'react';
import { HelpCircle, ChevronDown, Award, Home, Apple, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_STAFF_ROLES = new Set([
  'manager', 'supervisor', 'crew_leader', 'agent', 'site_agent', 'staff', 'cleaner',
]);
const RESIDENT_ROLES = new Set(['tenant', 'resident']);

// Role → { key, label, Icon } for the extra role-scoped tab.
const ROLE_TAB_META = {
  agent_success_faqs:   { key: 'agent_success',   label: 'Agent Success',        Icon: Award },
  resident_service_faqs:{ key: 'resident_guide',  label: 'Resident Service Guide', Icon: Home  },
};

const FAQ_GROUPS = [
  {
    key: 'property',
    category: 'Property Management',
    items: [
      {
        q: 'What services does NECS provide?',
        a: 'Noble Empire Cleaning Services (NECS) provides:\n• Valet trash collection\n• Recycling collection support\n• Bulk item removal\n• Overflow dumpster solutions\n• Dumpster treatment & trash room maintenance\n• Professional pressure washing\n• Professional interior & exterior window cleaning\n• Eco-friendly commercial cleaning & sanitizing\n• Common-area trash support services\n• Community sustainability support solutions',
      },
      {
        q: 'What makes NECS different from traditional valet trash companies?',
        a: 'NECS combines technology-driven transparency, recycling support, eco-friendly operational practices, and luxury-level customer service standards into one complete community support solution.\n\nOur services include QR-coded trash containers, automated reporting systems, resident notifications, and customizable service packages.',
      },
      {
        q: 'What are your valet trash collection schedules?',
        a: 'NECS valet trash and recycling collection runs Monday through Saturday.\n\n• SET-OUT WINDOW — residents place trash cans outside their door between 5:00 PM and 7:00 PM\n• COLLECTION WINDOW — primary route collection typically occurs between 7:00 PM and 9:00 PM (exact start time varies by property + contracted service bundle; some routes run later)\n• Once our agent has finished servicing your building for the night, we do not return until the next scheduled service day — please make sure trash is set out on time\n• CAN RETRIEVAL — MANDATORY: all trash cans must be taken back inside the apartment immediately after collection is complete',
      },
      {
        q: 'Does NECS provide recycling services?',
        a: 'Yes. Recycling services are available for communities with designated recycling dumpsters or approved recycling programs.\n\nResidents must place recyclables in clear or blue bags according to community recycling guidelines.',
      },
      {
        q: 'Are your employees insured and background checked?',
        a: 'Yes. All NECS employees are trained, background-checked, uniformed W2 employees operating under professional service standards.\n\nNECS also maintains insured operations.',
      },
      {
        q: 'Does NECS provide operational reporting?',
        a: 'Yes. The NECS Resident App and QR-code system provide:\n• Real-time collection tracking\n• Service verification\n• Violation reporting\n• Resident notifications\n• Management visibility support',
      },
      {
        q: 'Can service packages be customized?',
        a: 'Absolutely. NECS offers customized service programs tailored to the operational needs, property size, and budget of each community.',
      },
      {
        q: 'What additional community support services are available?',
        a: 'Depending on the service agreement, NECS may also provide:\n• Daily property trash pickup\n• Pressure washing services\n• Professional interior & exterior window cleaning\n• Dumpster enclosure treatment\n• Trash room maintenance\n• Bulk item removal\n• Eco-friendly amenities cleaning\n• Overflow dumpster solutions',
      },
      {
        q: 'Does NECS offer long-term partnership agreements?',
        a: 'Yes. NECS offers annual agreements, long-term service contracts, and customized partnership programs for multifamily communities and commercial properties.',
      },
    ],
  },
  {
    key: 'resident',
    category: 'Residents',
    items: [
      {
        q: 'Why was my trash not collected?',
        a: 'NECS runs primary-route valet trash collection between 7:00 PM and 9:00 PM, Monday to Saturday. Residents must place cans outside between 5:00 PM and 7:00 PM.\n\nTo qualify for collection:\n• Trash must be securely bagged and tied\n• Trash must be placed inside the provided NECS container\n• Maximum limit is two (2) bags per collection\n• Grocery bags and paper bags are not accepted\n• Cans must be outside during the 5–7 PM set-out window\n\nOnce our agent has finished servicing your building for the night, we do not return until your next scheduled service day. Install the NECS Resident App to receive instant "service in progress" and "service completed" notifications so you never miss the window.',
      },
      {
        q: 'When do I have to bring my trash can back inside?',
        a: 'MANDATORY: all trash cans must be taken back inside the apartment immediately after collection is complete.\n\n• Do not leave the can in the hallway, doorstep or walkway overnight\n• The NECS Resident App will push a "Service Completed — please bring your can inside" notification for your unit\n• Repeated violations of the retrieval rule are photo-documented and escalated to your Property Manager',
      },
      {
        q: 'I followed the guidelines and my trash was still not collected. What should I do?',
        a: 'We apologize for the inconvenience.\n\nResidents should document any missed collection through the NECS Resident App or by contacting customer support.\n\nResidents may also contact:\n• Email: support@nobleempirecs.com\n• Website: www.nobleempirecs.com\n• NECS Resident App (Google Play & Apple App Store)',
      },
      {
        q: 'Why did I receive a violation tag?',
        a: 'Violation tags are friendly reminders regarding valet trash procedures and community guidelines.\n\nCommon violations may include:\n• Improper bag usage\n• Containers left outside after collection hours\n• Excess trash bags\n• Loose trash or spills\n\nViolation fees, if applicable, are determined and issued by property management.',
      },
      {
        q: 'Why was I asked to bring my container back inside?',
        a: 'To help maintain a clean and attractive community appearance — and per NECS operational policy — all trash cans MUST be taken back inside the apartment immediately after collection is complete.\n\nContainers left outside overnight are logged as a violation and escalated to the Property Manager after three photo-documented incidents.',
      },
      {
        q: 'How does the recycling program work?',
        a: 'Recyclables are collected similarly to regular trash but must be placed in clear or blue recycling bags.\n\nTrash placed in black or white bags may be treated as regular garbage.\n\nResidents should also:\n• Flatten and bundle cardboard boxes\n• Avoid placing broken glass in bags\n• Follow local recycling guidelines',
      },
      {
        q: 'What items cannot be recycled?',
        a: 'Large, heavy, hazardous, or bulk items including furniture, appliances, televisions, and oversized materials are generally not accepted through standard recycling services.\n\nPlease review local recycling guidelines or contact property management for additional information.',
      },
      {
        q: 'Where can residents purchase blue recycling bags?',
        a: 'Blue recycling bags may be purchased at grocery stores, retail stores, and home improvement centers.\n\nSome leasing offices may also provide recycling bags.',
      },
      {
        q: 'What if my schedule does not allow me to follow valet trash guidelines?',
        a: 'Residents who may occasionally be unable to follow normal collection procedures should document the situation through the NECS Resident App.\n\nResidents may also contact property management for additional assistance.',
      },
      {
        q: 'Will residents receive a bill from NECS?',
        a: 'No. NECS services are contracted directly through property management.\n\nResidents should contact their leasing office for any billing-related questions.',
      },
      {
        q: 'Can residents opt out of valet trash services?',
        a: 'Most valet trash services are community-wide programs contracted through property management.\n\nResidents interested in opting out should contact their leasing office directly.',
      },
      {
        q: 'What happens if a trash container is lost, broken, or damaged?',
        a: 'Provided trash containers remain NECS property during the contract period.\n\nAdditional charges may apply for lost, damaged, or excessively dirty containers depending on property policies and contract terms.',
      },
      {
        q: 'Do residents have to use the provided trash container?',
        a: 'Yes. Residents are required to use the provided NECS trash container.\n\nUsing standardized containers helps maintain a cleaner and more visually organized community while supporting QR-code tracking and operational efficiency.',
      },
      {
        q: 'How does bulk item removal work?',
        a: 'Bulk item removal is scheduled based on the service agreement between NECS and your Property Management team.\n\n• Your Property Manager publishes the next bulk pickup day for the community through the NECS app\n• On that day, place bulk items next to the dumpster area or trash room by the pickup time set in the contract\n• Items placed outside the scheduled bulk day are not collected — please wait for the next notification\n• Install the NECS Resident App to receive the bulk pickup alert directly on your phone',
      },
      {
        q: 'How can I try the demo before signing up?',
        a: 'The NECS Resident and Property Management demos are gated by a company-issued access code.\n\nContact NECS directly to get your demo code and a personal walk-through of the app — our team will show you how registration, notifications, violations, bulk pickup and reporting work end-to-end.\n\n• Phone: (888) 241-3077\n• Email: support@nobleempirecs.com\n• Website: www.nobleempirecs.com',
      },
    ],
  },
  {
    key: 'commercial',
    category: 'Commercial Business',
    items: [
      {
        q: 'What types of commercial properties does NECS service?',
        a: 'NECS provides services for:\n• Commercial office buildings\n• Gas stations\n• Restaurants\n• Churches\n• Schools\n• Retail stores\n• Shopping centers\n• Medical offices\n• Warehouses\n• Other commercial facilities',
      },
      {
        q: 'What commercial services does NECS provide?',
        a: 'Commercial services may include:\n• Daily eco-friendly commercial cleaning\n• Pressure washing services\n• Professional interior & exterior window cleaning\n• Dumpster treatment & maintenance\n• Trash room maintenance\n• Waste management support\n• Recycling solutions\n• Overflow dumpster solutions\n• Sanitizing services',
      },
      {
        q: 'Does NECS use eco-friendly cleaning products?',
        a: 'Yes. NECS focuses on environmentally conscious cleaning solutions using non-toxic and eco-friendly products whenever possible.',
      },
      {
        q: 'Are commercial services available without contracts?',
        a: 'No. Commercial business services are currently provided through service agreements and contracts only.\n\nThis allows NECS to maintain consistent scheduling, quality standards, and operational reliability.',
      },
      {
        q: 'Can commercial clients combine multiple services?',
        a: 'Absolutely. Commercial clients may combine cleaning services, pressure washing, dumpster treatment, recycling support, and additional operational services into customized service packages.',
      },
      {
        q: 'Does NECS provide recurring commercial cleaning?',
        a: 'Yes. NECS offers daily, weekly, and customized recurring commercial cleaning schedules based on client operational needs.',
      },
      {
        q: 'What areas can NECS pressure wash?',
        a: 'Pressure washing services may include:\n• Sidewalks\n• Dumpster enclosures\n• Parking lots\n• Storefronts\n• Drive-thrus\n• Gas station surfaces\n• Trash rooms\n• Exterior building surfaces',
      },
      {
        q: 'What is included in dumpster treatment & trash room maintenance?',
        a: 'Dumpster treatment and trash room maintenance services may include:\n• Odor reduction treatment\n• Deep cleaning\n• Pressure washing\n• Sanitizing services\n• Trash buildup removal\n• Insect and pest reduction support',
      },
      {
        q: 'Does NECS offer professional interior & exterior window cleaning?',
        a: 'Yes. NECS offers professional interior & exterior window cleaning for leasing offices, clubhouses, fitness centers, lobbies, storefronts, and designated common areas.\n\nWindow cleaning is included in our two top property service tiers:\n• Noble Empire Executive Collection — twice annually (biannual)\n• Noble Empire Presidential Collection — every quarter (four times annually)',
      },
      {
        q: 'Is NECS insured for commercial operations?',
        a: 'Yes. NECS maintains insured operations and professional operational standards for both multifamily and commercial service programs.',
      },
      {
        q: 'How can commercial businesses request a consultation?',
        a: 'Commercial businesses may contact NECS directly to schedule a consultation, walkthrough, or customized service proposal.\n\nNoble Empire Cleaning Services\nPhone: (888) 241-3077\nEmail: support@nobleempirecs.com\nWebsite: www.nobleempirecs.com',
      },
    ],
  },
];

export default function ProfileFAQ({ role = '' } = {}) {
  const [activeKey, setActiveKey] = useState('');
  const [roleFaq, setRoleFaq] = useState(null); // { key, label, items, Icon }
  const isFieldStaff = FIELD_STAFF_ROLES.has((role || '').toLowerCase());
  const isResident   = RESIDENT_ROLES.has((role || '').toLowerCase());

  // Pull the role-scoped FAQ from the Central Knowledge Base so the Admin
  // team can update it in one place and Noble AI stays in sync.
  useEffect(() => {
    if (!role || (!isFieldStaff && !isResident)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/knowledge/role-faqs?role=${encodeURIComponent(role)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const meta = ROLE_TAB_META[data.key];
        if (meta && (data.items || []).length) {
          setRoleFaq({ ...meta, items: data.items });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [role, isFieldStaff, isResident]);

  // Compose the visible tabs. The role-scoped tab is prepended so it lands
  // in front of the shared "Property Management / Residents / Commercial"
  // trio and becomes the default landing tab for that role.
  const groups = useMemo(() => {
    if (roleFaq) {
      return [
        { key: roleFaq.key, category: roleFaq.label, Icon: roleFaq.Icon, items: roleFaq.items },
        ...FAQ_GROUPS,
      ];
    }
    return FAQ_GROUPS;
  }, [roleFaq]);

  // Reset active tab when the group list changes.
  useEffect(() => {
    setActiveKey(groups[0]?.key || '');
  }, [groups]);

  const active = groups.find((g) => g.key === activeKey) || groups[0];
  if (!active) return null;

  return (
    <section
      className="mt-8 pt-8 border-t"
      style={{ borderColor: 'rgba(201,169,98,0.25)' }}
      data-testid="profile-faq"
    >
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="h-5 w-5" style={{ color: '#C9A962' }} />
        <h3
          className="text-base font-bold uppercase tracking-wide"
          style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#000' }}
          data-testid="profile-faq-title"
        >
          Help &amp; FAQs
        </h3>
      </div>

      {/* Tab strip — role-scoped tab (Agent Success or Resident Service
          Guide) is inserted for field staff / residents; the shared 3-tab
          set (Property Management · Residents · Commercial Business) is
          always present. */}
      <div
        className="flex flex-wrap gap-2 border-b mb-4"
        style={{ borderColor: 'rgba(201,169,98,0.25)' }}
        role="tablist"
        aria-label="FAQ topics"
        data-testid="profile-faq-tabs"
      >
        {groups.map((g) => {
          const isActive = g.key === activeKey;
          const Icon = g.Icon;
          return (
            <button
              key={g.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveKey(g.key)}
              className="px-4 py-2 -mb-px text-sm font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-1.5"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                color: isActive ? '#C9A962' : '#6b7280',
                borderColor: isActive ? '#C9A962' : 'transparent',
                backgroundColor: 'transparent',
              }}
              data-testid={`profile-faq-tab-${g.key}`}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {g.category}
            </button>
          );
        })}
      </div>

      {/* Optional App Store / Google Play banner — only shown on the
          Resident Service Guide tab per iter-87 spec. */}
      {active.key === 'resident_guide' && (
        <div
          className="mb-3 flex flex-wrap gap-2 p-3 rounded-lg bg-zinc-900 border"
          style={{ borderColor: 'rgba(201,169,98,0.35)' }}
          data-testid="resident-guide-app-cta"
        >
          <p className="text-xs text-white flex-1 min-w-[220px]" style={{ lineHeight: 1.5 }}>
            Get the NECS Resident App for instant service alerts, missed-pickup reporting, and bulk item notifications.
          </p>
          <a
            href="https://apps.apple.com/us/app/necs-resident/id0000000000"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-black text-white text-xs font-semibold hover:bg-zinc-800"
            data-testid="resident-guide-app-store"
          >
            <Apple className="h-3.5 w-3.5" /> App Store
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.nobleempirecs.resident"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-black text-xs font-semibold hover:bg-[#EAB308]"
            style={{ backgroundColor: '#FFD60A' }}
            data-testid="resident-guide-play-store"
          >
            <Play className="h-3.5 w-3.5" /> Google Play
          </a>
        </div>
      )}

      {/* Accordion for the active tab */}
      <div className="space-y-2">
        {active.items.map((item, i) => (
          <details
            key={i}
            className="group rounded-lg border bg-white transition open:shadow-sm"
            style={{ borderColor: 'rgba(201,169,98,0.25)' }}
            data-testid={`profile-faq-item-${active.key}-${i}`}
          >
            <summary
              className="flex items-center justify-between px-4 py-3 cursor-pointer list-none text-sm font-semibold"
              style={{ color: '#111' }}
            >
              <span>{item.q}</span>
              <ChevronDown className="h-4 w-4 flex-shrink-0 ml-3 transition-transform group-open:rotate-180" style={{ color: '#C9A962' }} />
            </summary>
            <p
              className="px-4 pb-4 text-sm whitespace-pre-line"
              style={{ color: '#374151', lineHeight: 1.65 }}
            >
              {item.a}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}
